/**
 * Слой рисования интерактивной гиперболической карты.
 *
 * Все примитивы рисования хранятся в **координатах поверхности** (в канонической
 * системе фундаментальной области), независимо от текущего вида `V` и уровня
 * детализации (Требование 10.3). Ввод пользователя приходит в экранных
 * (пиксельных) координатах и переводится в координаты поверхности инверсией вида:
 * пиксель → точка диска `w` (обратная проекция рендерера) → точка поверхности
 * `z = V⁻¹(w)` (см. {@link DrawingLayer.screenToSurface}).
 *
 * «Учёт кривизны» (Требование 8.7): геометрия примитива задаётся в координатах
 * поверхности и отображается под текущим видом и геометрией. Между опорными
 * точками примитивов строятся **геодезические сегменты** модели Пуанкаре
 * (см. `geodesicArc` из `@langton/shared`), а не отрезки в экранных координатах,
 * поэтому линии/фигуры остаются корректными при любом виде и приближении.
 *
 * Векторные примитивы (`stroke`, `shape`) рендерятся через
 * {@link WebGLMapRenderer.drawVertices} тем же шейдерным конвейером, что и меш
 * карты. Растровая заливка (`fill`) — задача 17.2; здесь для неё предусмотрен
 * хук {@link DrawingLayerOptions.renderFill}.
 *
 * Маска стирания (`erase`, Требование 1) — обычный примитив стека: она
 * заливается фоновым цветом карты по геометрии радиуса вдоль геодезической
 * ломаной движения ластика (тот же приём тесселяции полосы, что и у «толстой»
 * кисти), визуально удаляя нижележащий по z-стеку контент (Требование 1.3).
 * Так как это обычный примитив, он корректно переносится во все соседние копии
 * под γ (Требование 4), участвует в сериализации/загрузке идентично на всех LOD
 * (Требование 1.6), а при отсутствии контента под маской фон поверх фона не
 * меняет изображение (Требование 1.8).
 *
 * Построение геометрии и перевод координат — чистые, тестируемые функции без
 * DOM/WebGL; собственно вызовы GL сосредоточены в тонком методе
 * {@link DrawingLayer.render}.
 *
 * Требования: 1.3, 1.6, 1.8, 8.7, 10.3
 */

import type {
  Complex,
  DrawingPrimitive,
  EraseMaskPrimitive,
  FillPrimitive,
  Geometry,
  Isometry,
  Mobius,
  ShapePrimitive,
  StrokePrimitive,
} from '@langton/shared';
import {
  apply,
  asIsometry,
  distance,
  distanceToRadius,
  fromPolar,
  hyperbolicGeometry,
  identity,
  modulus,
  translateFromOrigin,
} from '@langton/shared';
import {
  projectDiskToScreen,
  projectScreenToDisk,
  type ScreenPoint,
  type Viewport,
} from './map-view-controller.js';
import type { DrawMode, WebGLMapRenderer } from './webgl-map-renderer.js';

/** Значения по умолчанию для параметров тесселяции. */
const DEFAULTS = {
  /** Число отрезков на каждый геодезический сегмент кисти/линии. */
  segmentsPerGeodesic: 12,
  /** Число сегментов ломаной для аппроксимации круга/эллипса. */
  circleSegments: 48,
  /** Число сегментов при тесселяции кривой Безье. */
  bezierSegments: 32,
  /** Число сегментов ломаной диска маски стирания (Требование 1.3). */
  eraseSegments: 24,
} as const;

/** Фоновый цвет карты по умолчанию — цвет заливки маски стирания (Требование 1.3). */
const DEFAULT_BACKGROUND_COLOR = '#000000';

/** Радиус ластика по умолчанию (Требование 1.4), если он отсутствует/некорректен. */
const DEFAULT_ERASE_RADIUS = 10;

/**
 * Масштаб радиуса стирания: коэффициент перевода значения ползунка ластика
 * `[1, 40]` (хранимое поле {@link EraseMaskPrimitive.radius}) в **малый**
 * гиперболический радиус диска маски (Требования 10.1, 10.2).
 *
 * Прежнее поведение трактовало значение ползунка напрямую как радиус в единицах
 * гиперболической метрики, из-за чего даже радиус `1` (`distanceToRadius(1) =
 * tanh(0.5) ≈ 0.46`) покрывал около половины видимого диска, а значение по
 * умолчанию `10` (`tanh(5) ≈ 0.99991`) — практически весь диск. Домножение на
 * этот масштаб даёт `hyperbolicRadius = sliderValue · ERASER_RADIUS_SCALE`,
 * при котором евклидов радиус диска остаётся малой долей единичного диска для
 * всех значений ползунка (при `40` — `tanh(0.1) ≈ 0.0997`, ~10 %; при `10` —
 * `tanh(0.025) ≈ 0.025`, ~2.5 %). Хранение поля `radius` в координатах
 * поверхности не меняется, поэтому стирание остаётся инвариантным к уровню
 * детализации и отменяется одним шагом истории (Требование 10.5).
 */
export const ERASER_RADIUS_SCALE = 0.005;

/**
 * Максимальный евклидов радиус диска маски стирания. Радиус стирания задаётся
 * значением ползунка `[1, 40]`, масштабируемым в гиперболическую метрику через
 * {@link ERASER_RADIUS_SCALE} (Требование 1.4); при больших значениях
 * `distanceToRadius` приближается к `1` (граница диска). Радиус зажимается чуть
 * ниже границы, чтобы точки диска оставались строго внутри модели и вершинный
 * шейдер не давал вырожденных (нечисловых) координат.
 */
const MAX_ERASE_DISK_RADIUS = 1 - 1e-6;

/** Опции конфигурации {@link DrawingLayer}. */
export interface DrawingLayerOptions {
  /** Число отрезков на геодезический сегмент кисти/линии (`≥ 1`). */
  segmentsPerGeodesic?: number;
  /** Число сегментов ломаной круга/эллипса (`≥ 3`). */
  circleSegments?: number;
  /** Число сегментов тесселяции кривой Безье (`≥ 1`). */
  bezierSegments?: number;
  /** Число сегментов ломаной диска маски стирания (`≥ 3`, Требование 1.3). */
  eraseSegments?: number;
  /**
   * Фоновый цвет карты (hex), которым заливается маска стирания (Требование 1.3).
   * Маска стирания «удаляет» нижележащий контент, перекрывая его фоном; при
   * отсутствии контента под маской фон поверх фона не меняет изображение
   * (Требование 1.8). При отрисовке значение синхронизируется с текущим фоном
   * рендерера ({@link WebGLMapRenderer.getBackgroundColorHex}); по умолчанию
   * {@link DEFAULT_BACKGROUND_COLOR}.
   */
  backgroundColor?: string;
  /**
   * Хук рендеринга растровой заливки (`fill`-примитив, задача 17.2). Вызывается
   * для каждого `FillPrimitive` при отрисовке слоя. Если не задан — заливки
   * пропускаются (векторные примитивы всё равно отрисовываются).
   */
  renderFill?: (
    fill: FillPrimitive,
    renderer: WebGLMapRenderer,
    view: Mobius,
  ) => void;
}

/**
 * Команда отрисовки: набор вершин в координатах поверхности, режим и цвет.
 * Возвращается чистыми построителями геометрии и исполняется в
 * {@link DrawingLayer.render} через {@link WebGLMapRenderer.drawVertices}.
 */
export interface DrawCommand {
  /** Режим отрисовки WebGL-абстракции. */
  mode: DrawMode;
  /** Вершины в координатах поверхности. */
  vertices: Complex[];
  /** Цвет (hex). */
  color: string;
}

/** Округление координаты для устойчивой сериализации/сравнения. */
function cloneComplex(p: Complex): Complex {
  return { re: p.re, im: p.im };
}

/** Проверка, что точка — корректное комплексное число внутри диска. */
function isFiniteComplex(p: Complex | undefined | null): p is Complex {
  return (
    !!p &&
    typeof p.re === 'number' &&
    typeof p.im === 'number' &&
    Number.isFinite(p.re) &&
    Number.isFinite(p.im)
  );
}

/**
 * Проверка, что изометрия склейки (матрица SU(1,1) `{ alpha, beta }`) состоит из
 * конечных компонент. Вырожденная/почти-сингулярная изометрия копии может дать
 * нечисловые `alpha`/`beta` (NaN/Infinity); такую копию рендер пропускает,
 * сохраняя остальные копии и уже отрисованный контент (Требование 4.7).
 */
function isFiniteMobius(m: Mobius | undefined | null): m is Mobius {
  return !!m && isFiniteComplex(m.alpha) && isFiniteComplex(m.beta);
}

/**
 * Построить полилинию из точек поверхности, соединённых геодезическими
 * сегментами модели Пуанкаре (Требование 8.7). Каждая пара соседних точек
 * тесселируется в `segments` отрезков вдоль геодезической; стыковые точки не
 * дублируются. Результат — последовательность точек поверхности для отрисовки в
 * режиме `line-strip`.
 *
 * @param points опорные точки поверхности (`≥ 1`)
 * @param segments число отрезков на геодезический сегмент (`≥ 1`)
 */
export function buildGeodesicPolyline(
  points: Complex[],
  segments: number = DEFAULTS.segmentsPerGeodesic,
  geometry: Geometry = hyperbolicGeometry,
): Complex[] {
  const valid = points.filter(isFiniteComplex);
  if (valid.length === 0) {
    return [];
  }
  if (valid.length === 1) {
    return [cloneComplex(valid[0])];
  }
  const seg = Math.max(1, Math.floor(segments));
  const out: Complex[] = [cloneComplex(valid[0])];
  for (let i = 0; i < valid.length - 1; i++) {
    // Сегмент в метрике геометрии: гиперболическая дуга, евклидов отрезок
    // (прямая) или сферическая дуга (Требования 6, 8.7).
    const arc = geometry.geodesicArc(valid[i], valid[i + 1], seg);
    // Пропускаем первую точку дуги — она совпадает с последней добавленной.
    for (let j = 1; j < arc.length; j++) {
      out.push(cloneComplex(arc[j]));
    }
  }
  return out;
}

/** Четыре угла прямоугольника по двум противоположным углам (в координатах поверхности). */
export function rectangleCorners(a: Complex, b: Complex): Complex[] {
  return [
    { re: a.re, im: a.im },
    { re: b.re, im: a.im },
    { re: b.re, im: b.im },
    { re: a.re, im: b.im },
  ];
}

/**
 * Четыре угла квадрата по двум точкам: сторона берётся равной большему из
 * модулей смещений `|Δx|`, `|Δy|`, направление сохраняется по знаку смещения.
 */
export function squareCorners(a: Complex, b: Complex): Complex[] {
  const dx = b.re - a.re;
  const dy = b.im - a.im;
  const side = Math.max(Math.abs(dx), Math.abs(dy));
  const sx = dx < 0 ? -side : side;
  const sy = dy < 0 ? -side : side;
  return rectangleCorners(a, { re: a.re + sx, im: a.im + sy });
}

/**
 * Точки гиперболической окружности с центром `center`, проходящей через
 * `radiusPoint`. Радиус берётся как гиперболическое расстояние между точками;
 * точки окружности строятся на этом расстоянии во всех направлениях в кадре с
 * центром в `center` и переносятся обратно изометрией `translateFromOrigin`.
 * Возвращается замкнутый контур (`segments` точек, без дублирования замыкающей).
 */
export function buildCirclePoints(
  center: Complex,
  radiusPoint: Complex,
  segments: number = DEFAULTS.circleSegments,
): Complex[] {
  // Гиперболическое построение требует центр строго внутри диска (|center| < 1):
  // `distance`/`translateFromOrigin` неопределены на границе и вне диска. Для
  // евклидовых/сферических координат вне диска возвращаем пустой контур, чтобы
  // не бросать исключение (Требование 8; евклидова окружность — отдельная задача).
  if (!isFiniteComplex(center) || modulus(center) >= 1) {
    return [];
  }
  const count = Math.max(3, Math.floor(segments));
  const r = distanceToRadius(distance(center, radiusPoint));
  const g = translateFromOrigin(center);
  const pts: Complex[] = [];
  for (let i = 0; i < count; i++) {
    const theta = (2 * Math.PI * i) / count;
    pts.push(apply(g, fromPolar(r, theta)));
  }
  return pts;
}

/**
 * Замкнутый контур гиперболического диска маски стирания с центром `center` и
 * радиусом `hyperbolicRadius` (в единицах гиперболической метрики, Требование
 * 1.4). Диск строится в кадре с центром в `center`: точки на евклидовом радиусе
 * `distanceToRadius(hyperbolicRadius)` во всех направлениях переносятся обратно
 * изометрией `translateFromOrigin(center)`. Радиус зажимается чуть ниже границы
 * диска ({@link MAX_ERASE_DISK_RADIUS}), а нечисловой/отрицательный радиус даёт
 * пустой контур. Возвращается замкнутый контур (`segments` точек, без дубля
 * замыкающей); триангулируется в заливку веером.
 *
 * @param center центр диска в координатах поверхности
 * @param hyperbolicRadius радиус стирания в гиперболической метрике (`≥ 0`)
 * @param segments число точек ломаной (`≥ 3`)
 */
export function buildEraseDiskOutline(
  center: Complex,
  hyperbolicRadius: number,
  segments: number = DEFAULTS.eraseSegments,
): Complex[] {
  if (!isFiniteComplex(center) || !Number.isFinite(hyperbolicRadius)) {
    return [];
  }
  // Центр должен лежать строго внутри диска: `translateFromOrigin(center)`
  // неопределён на границе/вне диска. Для координат вне диска (евклидовы/
  // сферические карты) возвращаем пустой контур вместо исключения (Требование 8).
  if (modulus(center) >= 1) {
    return [];
  }
  const d = Math.max(0, hyperbolicRadius);
  const er = Math.min(distanceToRadius(d), MAX_ERASE_DISK_RADIUS);
  if (!(er > 0)) {
    return [];
  }
  const count = Math.max(3, Math.floor(segments));
  const g = translateFromOrigin(center);
  const pts: Complex[] = [];
  for (let i = 0; i < count; i++) {
    const theta = (2 * Math.PI * i) / count;
    pts.push(apply(g, fromPolar(er, theta)));
  }
  return pts;
}

/**
 * Точки гиперболического «эллипса» с центром `center` и полуосями, заданными
 * точками `axisX` (вдоль локальной вещественной оси) и `axisY` (вдоль мнимой).
 * В кадре с центром в `center` точки строятся как `(ax·cosθ, ay·sinθ)`, где
 * `ax`, `ay` — евклидовы радиусы, соответствующие гиперболическим расстояниям
 * до `axisX`/`axisY`, затем переносятся обратно. При отсутствии `axisY`
 * эллипс вырождается в окружность радиуса `ax`.
 */
export function buildEllipsePoints(
  center: Complex,
  axisX: Complex,
  axisY?: Complex,
  segments: number = DEFAULTS.circleSegments,
): Complex[] {
  // Центр строго внутри диска (см. {@link buildCirclePoints}); вне диска —
  // пустой контур без исключения (Требование 8).
  if (!isFiniteComplex(center) || modulus(center) >= 1) {
    return [];
  }
  const count = Math.max(3, Math.floor(segments));
  const ax = distanceToRadius(distance(center, axisX));
  const ay = axisY ? distanceToRadius(distance(center, axisY)) : ax;
  const g = translateFromOrigin(center);
  const pts: Complex[] = [];
  for (let i = 0; i < count; i++) {
    const theta = (2 * Math.PI * i) / count;
    pts.push(apply(g, { re: ax * Math.cos(theta), im: ay * Math.sin(theta) }));
  }
  return pts;
}

/**
 * Тесселяция кривой Безье произвольной степени по опорным точкам поверхности
 * (алгоритм Де Кастельжо в координатной карте поверхности). Возвращает
 * `segments + 1` точек. Для отрисовки полученные точки соединяются
 * геодезическими сегментами (см. {@link buildGeodesicPolyline}).
 */
export function buildBezierPoints(
  controls: Complex[],
  segments: number = DEFAULTS.bezierSegments,
): Complex[] {
  const valid = controls.filter(isFiniteComplex);
  if (valid.length === 0) {
    return [];
  }
  if (valid.length === 1) {
    return [cloneComplex(valid[0])];
  }
  const count = Math.max(1, Math.floor(segments));
  const pts: Complex[] = [];
  for (let i = 0; i <= count; i++) {
    pts.push(deCasteljau(valid, i / count));
  }
  return pts;
}

/** Вычислить точку кривой Безье в параметре `t` алгоритмом Де Кастельжо. */
function deCasteljau(controls: Complex[], t: number): Complex {
  let pts = controls.map(cloneComplex);
  while (pts.length > 1) {
    const next: Complex[] = [];
    for (let i = 0; i < pts.length - 1; i++) {
      next.push({
        re: pts[i].re + (pts[i + 1].re - pts[i].re) * t,
        im: pts[i].im + (pts[i + 1].im - pts[i].im) * t,
      });
    }
    pts = next;
  }
  return pts[0];
}

/** Триангуляция замкнутого контура веером от его гиперболического «барицентра». */
function fanTriangles(outline: Complex[]): Complex[] {
  if (outline.length < 3) {
    return [];
  }
  // Барицентр в координатной карте поверхности — достаточная опорная точка для
  // веерной заливки выпуклых/почти-выпуклых контуров (приближение; точная
  // растровая заливка — задача 17.2).
  let cx = 0;
  let cy = 0;
  for (const p of outline) {
    cx += p.re;
    cy += p.im;
  }
  const center: Complex = { re: cx / outline.length, im: cy / outline.length };
  const tris: Complex[] = [];
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i];
    const b = outline[(i + 1) % outline.length];
    tris.push(cloneComplex(center), cloneComplex(a), cloneComplex(b));
  }
  return tris;
}

/**
 * Слой векторных примитивов рисования карты.
 *
 * Жизненный цикл:
 * 1. `new DrawingLayer(options)` — параметры тесселяции и хук заливки.
 * 2. `add(...)` / `remove(...)` / `clear()` — управление примитивами.
 * 3. `screenToSurface(...)` — перевод ввода экран→поверхность (`V⁻¹`).
 * 4. `serialize()` / `load(...)` — сериализация/восстановление для API.
 * 5. `render(renderer, view)` — отрисовка примитивов через рендерер.
 */
export class DrawingLayer {
  /** Примитивы в порядке z (последний элемент — сверху). */
  private primitives: DrawingPrimitive[] = [];
  private readonly options: Required<
    Omit<DrawingLayerOptions, 'renderFill' | 'backgroundColor'>
  > & { renderFill?: DrawingLayerOptions['renderFill'] };
  /**
   * Фоновый цвет карты (hex) — цвет заливки маски стирания (Требование 1.3).
   * Синхронизируется с рендерером при {@link DrawingLayer.render}.
   */
  private backgroundColor: string;

  constructor(options: DrawingLayerOptions = {}) {
    this.options = {
      segmentsPerGeodesic:
        options.segmentsPerGeodesic ?? DEFAULTS.segmentsPerGeodesic,
      circleSegments: options.circleSegments ?? DEFAULTS.circleSegments,
      bezierSegments: options.bezierSegments ?? DEFAULTS.bezierSegments,
      eraseSegments: options.eraseSegments ?? DEFAULTS.eraseSegments,
      renderFill: options.renderFill,
    };
    this.backgroundColor = options.backgroundColor ?? DEFAULT_BACKGROUND_COLOR;
  }

  /**
   * Задать фоновый цвет карты (hex), которым заливается маска стирания
   * (Требование 1.3). Обычно вызывать не нужно — {@link DrawingLayer.render}
   * синхронизирует его с текущим фоном рендерера.
   */
  setBackgroundColor(hex: string): void {
    if (typeof hex === 'string' && hex.length > 0) {
      this.backgroundColor = hex;
    }
  }

  /** Текущий фоновый цвет заливки маски стирания (hex). */
  getBackgroundColor(): string {
    return this.backgroundColor;
  }

  /**
   * Добавить примитив (в координатах поверхности) поверх остальных.
   * Примитив клонируется, чтобы слой владел собственной копией.
   *
   * @returns z-индекс добавленного примитива (позиция в стеке)
   */
  add(primitive: DrawingPrimitive): number {
    this.primitives.push(clonePrimitive(primitive));
    return this.primitives.length - 1;
  }

  /** Примитив по z-индексу либо `undefined`. */
  get(index: number): DrawingPrimitive | undefined {
    const p = this.primitives[index];
    return p ? clonePrimitive(p) : undefined;
  }

  /** Все примитивы (копии) в порядке z. */
  getAll(): DrawingPrimitive[] {
    return this.primitives.map(clonePrimitive);
  }

  /** Число примитивов в слое. */
  get size(): number {
    return this.primitives.length;
  }

  /** Удалить примитив по z-индексу. Возвращает `true`, если элемент существовал. */
  remove(index: number): boolean {
    if (index < 0 || index >= this.primitives.length) {
      return false;
    }
    this.primitives.splice(index, 1);
    return true;
  }

  /** Очистить слой. */
  clear(): void {
    this.primitives = [];
  }

  /** Переместить примитив на самый верх z-стека. */
  bringToFront(index: number): boolean {
    if (index < 0 || index >= this.primitives.length) {
      return false;
    }
    const [p] = this.primitives.splice(index, 1);
    this.primitives.push(p);
    return true;
  }

  /** Переместить примитив в самый низ z-стека. */
  sendToBack(index: number): boolean {
    if (index < 0 || index >= this.primitives.length) {
      return false;
    }
    const [p] = this.primitives.splice(index, 1);
    this.primitives.unshift(p);
    return true;
  }

  /**
   * Перевести пиксельные координаты холста в координаты поверхности `V⁻¹(w)`
   * (Требование 10.3): пиксель → точка диска `w` в кадре вида (обратная проекция
   * рендерера) → точка поверхности `z = V⁻¹(w)`.
   *
   * @param px координата X в пикселях
   * @param py координата Y в пикселях
   * @param viewport размеры холста в пикселях
   * @param view текущий вид `V`
   * @param scale масштаб отображения диска
   * @returns точка поверхности (координаты фундаментальной области)
   */
  screenToSurface(
    px: number,
    py: number,
    viewport: Viewport,
    view: Mobius | Isometry,
    scale: number,
    geometry: Geometry = hyperbolicGeometry,
  ): Complex {
    const w = projectScreenToDisk(px, py, viewport, scale);
    // z = V⁻¹(w) в действии геометрии; для антиголоморфного вида инверсия честно
    // учитывает обращение ориентации (Требование 8). Для матрицы Mobius в
    // гиперболической геометрии совпадает с прежним apply(invert(view), w).
    return geometry.apply(geometry.invert(asIsometry(view)), w);
  }

  /**
   * Перевести точку поверхности в пиксельные координаты холста — обратно к
   * {@link screenToSurface}: `screen = project(V(z))`.
   *
   * @param z точка поверхности
   * @param viewport размеры холста в пикселях
   * @param view текущий вид `V`
   * @param scale масштаб отображения диска
   */
  surfaceToScreen(
    z: Complex,
    viewport: Viewport,
    view: Mobius | Isometry,
    scale: number,
    geometry: Geometry = hyperbolicGeometry,
  ): ScreenPoint {
    return projectDiskToScreen(geometry.apply(asIsometry(view), z), viewport, scale);
  }

  /**
   * Сериализовать слой в массив примитивов (готовый JSON для эндпоинта
   * `PUT /drawings`). Точки {@link Complex} сохраняются как есть и корректно
   * восстанавливаются при {@link load}.
   */
  serialize(): DrawingPrimitive[] {
    return this.primitives.map(clonePrimitive);
  }

  /** Псевдоним {@link serialize} для `JSON.stringify`. */
  toJSON(): DrawingPrimitive[] {
    return this.serialize();
  }

  /**
   * Загрузить примитивы, заменив текущее содержимое слоя. Некорректные записи
   * (неизвестный `kind`, отсутствующие координаты) отбрасываются.
   */
  load(primitives: DrawingPrimitive[]): void {
    this.primitives = [];
    for (const p of primitives ?? []) {
      const cloned = sanitizePrimitive(p);
      if (cloned) {
        this.primitives.push(cloned);
      }
    }
  }

  /**
   * Восстановить слой из сериализованных данных: JSON-строки или уже
   * разобранного массива примитивов. Эквивалент `load(JSON.parse(...))`.
   */
  deserialize(data: string | DrawingPrimitive[]): void {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    if (!Array.isArray(parsed)) {
      throw new Error('DrawingLayer.deserialize: expected an array of primitives');
    }
    this.load(parsed as DrawingPrimitive[]);
  }

  /** Создать слой из сериализованных данных. */
  static fromJSON(
    data: string | DrawingPrimitive[],
    options: DrawingLayerOptions = {},
  ): DrawingLayer {
    const layer = new DrawingLayer(options);
    layer.deserialize(data);
    return layer;
  }

  /**
   * Построить команды отрисовки для одного примитива в координатах поверхности
   * (чистая функция, тестируемая без WebGL). `fill`-примитивы команд не дают —
   * они отрисовываются растровым хуком (задача 17.2).
   */
  buildCommands(
    primitive: DrawingPrimitive,
    geometry: Geometry = hyperbolicGeometry,
  ): DrawCommand[] {
    switch (primitive.kind) {
      case 'stroke':
        return this.buildStrokeCommands(primitive, geometry);
      case 'shape':
        return this.buildShapeCommands(primitive, geometry);
      case 'erase':
        return this.buildEraseCommands(primitive, geometry);
      case 'fill':
      default:
        return [];
    }
  }

  /**
   * Команды для маски стирания (Требование 1.3): заливка фоновым цветом карты
   * области радиуса {@link EraseMaskPrimitive.radius} вдоль геодезической ломаной
   * движения ластика. Значение ползунка `[1, 40]` переводится в малый
   * гиперболический радиус через {@link ERASER_RADIUS_SCALE}, чтобы область
   * стирания оставалась небольшой локальной, а не покрывала почти весь диск
   * (Требования 10.1, 10.2). Ломаная точек поверхности сначала тесселируется в
   * геодезические сегменты (учёт кривизны), затем в каждой её точке строится
   * гиперболический диск ({@link buildEraseDiskOutline}), триангулируемый веером;
   * объединение дисков покрывает «полосу» радиуса вдоль пути (тот же приём, что
   * и у «толстой» кисти).
   *
   * Команда окрашивается фоновым цветом карты, поэтому при отрисовке поверх
   * нижележащих по z-стеку примитивов она их визуально удаляет (Требование 1.3),
   * а на участках без контента фон поверх фона не меняет изображение
   * (Требование 1.8). Геометрия строится в координатах поверхности, что
   * гарантирует идентичность на всех LOD (Требование 1.6).
   */
  private buildEraseCommands(
    erase: EraseMaskPrimitive,
    geometry: Geometry = hyperbolicGeometry,
  ): DrawCommand[] {
    const path = buildGeodesicPolyline(
      erase.points,
      this.options.segmentsPerGeodesic,
      geometry,
    );
    if (path.length === 0) {
      return [];
    }
    // Значение ползунка `[1, 40]` переводится в малый гиперболический радиус
    // домножением на ERASER_RADIUS_SCALE, чтобы область стирания оставалась
    // небольшой локальной областью вдоль траектории, а не покрывала почти весь
    // видимый диск (Требования 10.1, 10.2).
    const hyperbolicRadius = Math.max(0, erase.radius) * ERASER_RADIUS_SCALE;
    const vertices: Complex[] = [];
    for (const center of path) {
      const outline = buildEraseDiskOutline(
        center,
        hyperbolicRadius,
        this.options.eraseSegments,
      );
      if (outline.length >= 3) {
        for (const p of fanTriangles(outline)) {
          vertices.push(p);
        }
      }
    }
    if (vertices.length < 3) {
      return [];
    }
    return [{ mode: 'triangles', vertices, color: this.backgroundColor }];
  }

  /** Команды для кисти: одна геодезическая полилиния. */
  private buildStrokeCommands(
    stroke: StrokePrimitive,
    geometry: Geometry = hyperbolicGeometry,
  ): DrawCommand[] {
    const vertices = buildGeodesicPolyline(
      stroke.points,
      this.options.segmentsPerGeodesic,
      geometry,
    );
    if (vertices.length < 2) {
      return [];
    }
    return [{ mode: 'line-strip', vertices, color: stroke.color }];
  }

  /** Команды для фигуры: контур (обводка) и/или заливка треугольниками. */
  private buildShapeCommands(
    shape: ShapePrimitive,
    geometry: Geometry = hyperbolicGeometry,
  ): DrawCommand[] {
    const outline = this.buildShapeOutline(shape, geometry);
    if (outline.length < 2) {
      return [];
    }
    const commands: DrawCommand[] = [];
    const closed = isClosedShape(shape.shape);

    if (shape.fill && closed && outline.length >= 3) {
      const tris = fanTriangles(outline);
      if (tris.length >= 3) {
        commands.push({ mode: 'triangles', vertices: tris, color: shape.color });
      }
    }
    if (shape.stroke || !shape.fill) {
      commands.push({
        mode: closed ? 'line-loop' : 'line-strip',
        vertices: outline,
        color: shape.color,
      });
    }
    return commands;
  }

  /**
   * Построить контур фигуры в координатах поверхности. Рёбра многоугольных фигур
   * (линия/прямоугольник/квадрат) тесселируются в геодезические дуги; круг,
   * эллипс и кривая Безье — в соответствующие ломаные.
   */
  private buildShapeOutline(
    shape: ShapePrimitive,
    geometry: Geometry = hyperbolicGeometry,
  ): Complex[] {
    const pts = shape.points.filter(isFiniteComplex);
    const seg = this.options.segmentsPerGeodesic;
    switch (shape.shape) {
      case 'line': {
        if (pts.length < 2) {
          return [];
        }
        return buildGeodesicPolyline([pts[0], pts[1]], seg, geometry);
      }
      case 'rectangle': {
        if (pts.length < 2) {
          return [];
        }
        const corners =
          pts.length >= 4 ? pts.slice(0, 4) : rectangleCorners(pts[0], pts[1]);
        return geodesicClosedPolyline(corners, seg, geometry);
      }
      case 'square': {
        if (pts.length < 2) {
          return [];
        }
        const corners =
          pts.length >= 4 ? pts.slice(0, 4) : squareCorners(pts[0], pts[1]);
        return geodesicClosedPolyline(corners, seg, geometry);
      }
      case 'circle': {
        if (pts.length < 2) {
          return [];
        }
        return buildCirclePoints(pts[0], pts[1], this.options.circleSegments);
      }
      case 'ellipse': {
        if (pts.length < 2) {
          return [];
        }
        return buildEllipsePoints(
          pts[0],
          pts[1],
          pts[2],
          this.options.circleSegments,
        );
      }
      case 'bezier': {
        if (pts.length < 2) {
          return [];
        }
        const samples = buildBezierPoints(pts, this.options.bezierSegments);
        // Соединяем сэмплы кривой геодезическими сегментами.
        return buildGeodesicPolyline(samples, seg, geometry);
      }
      default:
        return [];
    }
  }

  /**
   * Отрисовать все примитивы слоя через рендерер под текущим видом
   * (Требования 8.7, 10.3) во всех видимых копиях фундаментальной области
   * (Требование 4.1, 4.6). Векторные примитивы рисуются
   * {@link WebGLMapRenderer.drawVertices}; растровые заливки — через хук
   * {@link DrawingLayerOptions.renderFill}, если он задан. Маска стирания
   * (`erase`, Требование 1.3) — обычный примитив стека: её геометрия заливается
   * фоновым цветом карты и потому визуально удаляет нижележащие по z-стеку
   * примитивы; будучи частью стека, она переносится во все копии под γ и
   * отменяется как один шаг истории.
   *
   * Каждый примитив хранится в координатах поверхности. Для каждой копии `γ`
   * (изометрии склейки из `LodManager.getVisibleCopies`) его координаты
   * поверхности `z` отображаются `apply(γ, z)` **до** применения вида, поэтому
   * один и тот же примитив рисуется во всех копиях со своей изометрией склейки
   * (Требование 4.6). Команда строится один раз в координатах поверхности и
   * переиспользуется для всех копий. Для растровых заливок эквивалентное
   * действие достигается композицией `compose(view, γ)`, передаваемой хуку.
   *
   * По умолчанию `copies = [identity()]` — единственная (текущая) копия, что
   * сохраняет прежнее однокопийное поведение: `apply(identity, z) = z` и
   * `compose(view, identity) = view`.
   *
   * @param renderer рендерер карты
   * @param view вид `V` (по умолчанию текущий вид рендерера)
   * @param scale масштаб отображения (если задан — применяется к рендереру)
   * @param copies список копий области `γ` (по умолчанию `[identity()]`)
   */
  render(
    renderer: WebGLMapRenderer,
    view: Mobius | Isometry = renderer.getView(),
    scale?: number,
    copies: (Mobius | Isometry)[] = [identity()],
    geometry: Geometry = hyperbolicGeometry,
  ): void {
    if (scale !== undefined) {
      renderer.setScale(scale);
    }
    // Синхронизировать цвет заливки маски стирания с текущим фоном карты
    // (Требование 1.3): маска перекрывает нижележащий контент фоновым цветом,
    // а на участках без контента фон поверх фона не меняет изображение
    // (Требование 1.8).
    if (typeof renderer.getBackgroundColorHex === 'function') {
      this.backgroundColor = renderer.getBackgroundColorHex();
    }
    // Вид и копии приводятся к обобщённым изометриям (матрица + признак
    // обращения ориентации). Для матриц Mobius в гиперболической геометрии
    // действие совпадает с прежним (обратная совместимость).
    const viewIso = asIsometry(view);
    // Пустой список копий трактуем как единственную текущую копию, сохраняя
    // прежнее поведение и гарантируя отрисовку контента хотя бы один раз.
    const rawCopies = copies.length > 0 ? copies : [identity()];
    const copyIsometries = rawCopies.map(asIsometry);
    for (const primitive of this.primitives) {
      if (primitive.kind === 'fill') {
        for (const gamma of copyIsometries) {
          // Композиция `V∘γ` для растровой заливки: если изометрия склейки
          // копии вырождена и композиция нечисловая, пропускаем только эту
          // копию, сохраняя остальные копии и уже отрисованный контент
          // (Требование 4.7).
          const composed = geometry.compose(viewIso, gamma);
          if (!isFiniteMobius(composed.matrix)) {
            continue;
          }
          this.options.renderFill?.(primitive, renderer, composed.matrix);
        }
        continue;
      }
      for (const cmd of this.buildCommands(primitive, geometry)) {
        for (const gamma of copyIsometries) {
          // Координаты поверхности примитива отображаются действием копии γ
          // (с учётом геометрии и обращения ориентации, Требование 8) ДО
          // применения вида рендерером тем же конвейером.
          const vertices = cmd.vertices.map((z) => geometry.apply(gamma, z));
          // Если применение γ дало хотя бы одну нечисловую вершину
          // (NaN/Infinity от вырожденной/почти-сингулярной изометрии),
          // пропускаем только эту копию данного примитива, продолжая с
          // остальными копиями и не затрагивая уже отрисованный контент
          // (Требование 4.7).
          if (!vertices.every(isFiniteComplex)) {
            continue;
          }
          renderer.drawVertices(vertices, cmd.mode, cmd.color, viewIso);
        }
      }
    }
  }
}

/** Ёмкость истории рисования по умолчанию (Требование 10.3: не менее 5 шагов). */
const DEFAULT_HISTORY_CAPACITY = 5;

/**
 * Ограниченный стек снимков состояния {@link DrawingLayer} для отмены действий
 * рисования (Ctrl+Z, Требование 10).
 *
 * Один снимок — это сериализованный массив примитивов слоя (результат
 * {@link DrawingLayer.serialize}) в состоянии **до** очередного действия
 * (мазок, фигура, заливка, стирание, установка маркера). Такое представление не
 * зависит от текущего вида/LOD: отмена восстанавливается простым
 * {@link DrawingLayer.load} предыдущего снимка.
 *
 * Стек ограничен по ёмкости (по умолчанию 5, Требование 10.3): при переполнении
 * вытесняется самый старый снимок, остальные остаются доступными для отмены в
 * порядке их совершения (Требование 10.4). Отмена возвращает снимки в порядке,
 * обратном их добавлению (LIFO), пока история не станет пустой.
 *
 * Класс владеет собственными глубокими копиями снимков: `push` клонирует вход, а
 * `undo` отдаёт снятую копию вызывающему коду, поэтому внешние мутации не влияют
 * на сохранённую историю.
 *
 * Требования: 10.1, 10.3, 10.4
 */
export class DrawingHistory {
  /** Снимки в порядке добавления (последний элемент — самый свежий). */
  private snapshots: DrawingPrimitive[][] = [];
  /** Максимальное число хранимых снимков (`≥ 1`). */
  private readonly capacity: number;

  /**
   * @param capacity максимальное число хранимых шагов отмены (по умолчанию 5,
   *   Требование 10.3). Значения меньше 1 или нечисловые приводятся к 1.
   */
  constructor(capacity: number = DEFAULT_HISTORY_CAPACITY) {
    this.capacity =
      Number.isFinite(capacity) && capacity >= 1 ? Math.floor(capacity) : 1;
  }

  /**
   * Протолкнуть снимок состояния слоя (одно завершённое действие — один шаг,
   * Требование 10.1). Снимок клонируется. При превышении ёмкости вытесняется
   * самый старый снимок (Требование 10.4).
   */
  push(snapshot: DrawingPrimitive[]): void {
    this.snapshots.push(cloneSnapshot(snapshot));
    while (this.snapshots.length > this.capacity) {
      this.snapshots.shift();
    }
  }

  /** Есть ли хотя бы один сохранённый шаг, доступный для отмены. */
  canUndo(): boolean {
    return this.snapshots.length > 0;
  }

  /**
   * Снять последний сохранённый снимок и вернуть его (состояние слоя до
   * последнего действия). Возвращает `null`, если история пуста (Требование
   * 10.5). Повторные вызовы отменяют шаги в порядке LIFO (Требование 10.7).
   */
  undo(): DrawingPrimitive[] | null {
    return this.snapshots.pop() ?? null;
  }

  /** Число сохранённых шагов, доступных для отмены. */
  size(): number {
    return this.snapshots.length;
  }
}

/**
 * Глубокая копия снимка слоя (массива примитивов в координатах поверхности).
 * Снимки — плоские, сериализуемые данные (точки + скалярные поля), поэтому
 * структурное копирование точно воспроизводит любой вид примитива, включая
 * маску стирания.
 */
function cloneSnapshot(snapshot: DrawingPrimitive[]): DrawingPrimitive[] {
  return (snapshot ?? []).map((primitive) => structuredClone(primitive));
}

/** Тесселировать замкнутый многоугольник в геодезический контур (без замыкающей точки). */
function geodesicClosedPolyline(
  corners: Complex[],
  segments: number,
  geometry: Geometry = hyperbolicGeometry,
): Complex[] {
  const valid = corners.filter(isFiniteComplex);
  if (valid.length < 2) {
    return [];
  }
  const seg = Math.max(1, Math.floor(segments));
  const out: Complex[] = [];
  for (let i = 0; i < valid.length; i++) {
    const a = valid[i];
    const b = valid[(i + 1) % valid.length];
    const arc = geometry.geodesicArc(a, b, seg);
    // Добавляем все точки, кроме последней (она — начало следующего ребра),
    // чтобы контур не содержал дубликатов на стыках и корректно замыкался.
    for (let j = 0; j < arc.length - 1; j++) {
      out.push(cloneComplex(arc[j]));
    }
  }
  return out;
}

/** Замкнутая ли фигура (имеет ли внутреннюю область для заливки). */
function isClosedShape(shape: ShapePrimitive['shape']): boolean {
  return shape !== 'line' && shape !== 'bezier';
}

/** Глубокое клонирование примитива (координаты поверхности). */
function clonePrimitive(primitive: DrawingPrimitive): DrawingPrimitive {
  switch (primitive.kind) {
    case 'stroke':
      return {
        kind: 'stroke',
        points: primitive.points.map(cloneComplex),
        width: primitive.width,
        color: primitive.color,
      };
    case 'shape':
      return {
        kind: 'shape',
        shape: primitive.shape,
        points: primitive.points.map(cloneComplex),
        fill: primitive.fill,
        stroke: primitive.stroke,
        color: primitive.color,
        width: primitive.width,
      };
    case 'fill':
      return {
        kind: 'fill',
        seed: cloneComplex(primitive.seed),
        color: primitive.color,
        level: primitive.level,
        mask: primitive.mask,
      };
    case 'erase':
      return {
        kind: 'erase',
        points: primitive.points.map(cloneComplex),
        radius: primitive.radius,
      };
    default:
      // Неизвестный вид — возвращаем поверхностную копию.
      return { ...(primitive as DrawingPrimitive) };
  }
}

/**
 * Проверить и клонировать примитив при загрузке. Возвращает `null` для
 * записей с неизвестным `kind` или недостающими обязательными координатами.
 */
function sanitizePrimitive(primitive: DrawingPrimitive): DrawingPrimitive | null {
  if (!primitive || typeof primitive !== 'object') {
    return null;
  }
  switch (primitive.kind) {
    case 'stroke':
    case 'shape': {
      if (!Array.isArray(primitive.points)) {
        return null;
      }
      const points = primitive.points.filter(isFiniteComplex).map(cloneComplex);
      if (points.length === 0) {
        return null;
      }
      if (primitive.kind === 'stroke') {
        return {
          kind: 'stroke',
          points,
          width: numberOr(primitive.width, 1),
          color: stringOr(primitive.color, '#000000'),
        };
      }
      return {
        kind: 'shape',
        shape: primitive.shape,
        points,
        fill: Boolean(primitive.fill),
        stroke: primitive.stroke !== false,
        color: stringOr(primitive.color, '#000000'),
        width: numberOr(primitive.width, 1),
      };
    }
    case 'fill': {
      if (!isFiniteComplex(primitive.seed)) {
        return null;
      }
      return {
        kind: 'fill',
        seed: cloneComplex(primitive.seed),
        color: stringOr(primitive.color, '#000000'),
        level: numberOr(primitive.level, 0),
        mask: stringOr(primitive.mask, ''),
      };
    }
    case 'erase': {
      // Маска стирания хранится в координатах поверхности и восстанавливается
      // идентично на всех LOD (Требование 1.6). Радиус сохраняется как есть
      // (нормализация в [1, 40] выполняется на этапе создания в drawing-tools),
      // чтобы round-trip сериализации не менял примитив.
      if (!Array.isArray(primitive.points)) {
        return null;
      }
      const points = primitive.points.filter(isFiniteComplex).map(cloneComplex);
      if (points.length === 0) {
        return null;
      }
      return {
        kind: 'erase',
        points,
        radius: numberOr(primitive.radius, DEFAULT_ERASE_RADIUS),
      };
    }
    default:
      return null;
  }
}

/** Число `value`, если конечно, иначе `fallback`. */
function numberOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** Строка `value`, если это строка, иначе `fallback`. */
function stringOr(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}
